import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import genericStyles from '../GenericComponents/GenericStyles.module.css';
import styles from './SimpleSearchBox.module.css';
import { ccConfig, CC, Search, SearchType } from '../CC/CC';
import { CCProvider, useCCContext } from '../CC/CCContext';
import GenericIconButton from '../GenericComponents/GenericIconButton';
import BrowseValues from './BrowseValues';
import AdvancedSearch from './AdvancedSearch';
import * as ccUtils from '../CC/utils';

interface TypeAheadItem {
  word: string;
}

interface SearchBoxProps {
  Field?: string;
  Fields?: "simple" | "advanced";
  showMapCallback?: (showMap: boolean) => void;
  showSearchButton?: boolean;
  id: string;
  searchTriggered?: boolean;
}

interface SearchValue {
  source: string;
  text: string;
}

function getDefaultField(fieldsType: "simple" | "advanced"): string {
  const fields = fieldsType === "simple" ? ccConfig.simpleSearchFields : ccConfig.simpleSearchFields;
  return Object.keys(fields)[0];
}

function SimpleSearchBox({ Field, Fields, id, showMapCallback, showSearchButton = false, searchTriggered }: SearchBoxProps) {
  const { t } = useTranslation();
  const instanceRef = useRef({});
  const { user, cc, setCc, first, setFirst, recordsPerPage, setRecordsPerPage, viewMode, setViewMode, browseMode, setBrowseMode, data, setData } = useCCContext();

  const existingSearch = useExistingSearch(cc, id, Field);

  const searchFields = Fields === "simple" ? ccConfig.simpleSearchFields : ccConfig.simpleSearchFields;
  const [selectedField, setSelectedField] = useState<string>(existingSearch.field || getDefaultField(Fields || "simple"));
  const [searchValues, setSearchValues] = useState<SearchValue[]>(existingSearch.searchValues);
  const [inputValue, setInputValue] = useState<string>("");
  const modalSearchValuesRef = useRef<HTMLDivElement>(null);
  const [modalSearchValuesPosition, setModalSearchValuesPosition] = useState<{ x: number, y: number } | null>(null);
  const [typeAheadResults, setTypeAheadResults] = useState<TypeAheadItem[]>([]);
  const [typeAheadShowing, setTypeAheadShowing] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const [isBrowseValuesModalOpen, setIsBrowseValuesModalOpen] = useState(false);
  const [isAdvancedModalOpen, setIsAdvancedModalOpen] = useState(false);
  const [prevCc, setPrevCc] = useState<CC>(new CC());

  const typeAheadRef = useRef<HTMLDivElement>(null);
  const inputTextBoxRef = useRef<HTMLInputElement>(null);
  const lastFetchedInputRef = useRef('');

  useEffect(() => {
    if (searchTriggered) {
      searchUserTypedText();
    }
  }, [searchTriggered]);


  function useExistingSearch(cc: any, id: string, Field?: string): SearchType {
    if (!cc || !cc.Searches) {
      // Handle the error scenario, possibly log it or return a default value
      return new Search(id, Field || "", []);
    }
    return cc.Searches.find((search: Search) => search.identifier === id) || new Search(id, Field || "", []);
  }

  const showSearchValues = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    let x = 0;
    let y = 0;

    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();

    // Try to find a parent modal of the clicked element
    const parentModal = target.closest('.parentModalClass');
    if (parentModal) {
      const modalRect = parentModal.getBoundingClientRect();
      x = rect.left - modalRect.left + window.scrollX;
      y = rect.bottom + 2 - modalRect.top + window.scrollY;
    } else {
      x = rect.left + window.scrollX;
      y = rect.bottom + 2 + window.scrollY;
    }

    setModalSearchValuesPosition({
      x: x,
      y: y
    });
  };

  const clearSearch = () => {
    setSearchValues([]);
    setInputValue("");
    /*
    setCc({
      ...cc,
      simpleSearchField: "",
      simpleSearchValue: [],
    });
    */
    setFirst(1);
  };

  const openAdvancedModal = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    setPrevCc({ ...cc }); // Make a copy of the current cc
    setIsAdvancedModalOpen(true);
  };

  const closeAdvancedModal = () => {
    setIsAdvancedModalOpen(false);
  };

  const openBrowseValuesModal = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    console.log('openBrowseValuesModal');
    event.preventDefault();
    setIsBrowseValuesModalOpen(true);
  };

  const closeBrowseValuesModal = (update: boolean, values?: string[]) => {
    console.log('closeBrowseValuesModal');
    if (update && values) {
      console.log('handleLocalSelectedBrowseValues');
      console.log(values);
      const newValues = values.map(value => ({ source: 'facet', text: value }));
      const updatedSearchValues = searchValues.filter(value => value.source !== 'facet' || values.includes(value.text));
      const uniqueNewValues = newValues.filter(newValue => !updatedSearchValues.some(value => value.text === newValue.text));
      setSearchValues([...updatedSearchValues, ...uniqueNewValues]);

      setFirst(1);
    }
    setIsBrowseValuesModalOpen(false);
  };

  const handleLocalSelectedBrowseValues = (values: string[]) => {
    console.log('handleLocalSelectedBrowseValues');
    console.log(values);
    const newValues = values.map(value => ({ source: 'facet', text: value }));
    const updatedSearchValues = searchValues.filter(value => value.source !== 'facet' || values.includes(value.text));
    const uniqueNewValues = newValues.filter(newValue => !updatedSearchValues.some(value => value.text === newValue.text));
    setSearchValues([...updatedSearchValues, ...uniqueNewValues]);

    setFirst(1);
  };

  useEffect(() => {
    if (!ccUtils.isEqual(searchValues, existingSearch.searchValues)) {
      setSearchValues(existingSearch.searchValues);
    }
  }, [existingSearch.searchValues]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalSearchValuesRef.current && !modalSearchValuesRef.current.contains(event.target as Node) && modalSearchValuesPosition) {
        setModalSearchValuesPosition(null);
      }

      // Check for clicks outside of the typeAheadRef
      if (typeAheadRef.current && !typeAheadRef.current.contains(event.target as Node) && typeAheadShowing) {
        setHighlightedIndex(null);
        setTypeAheadShowing(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [modalSearchValuesPosition, typeAheadShowing]);

  const handleDeleteValue = (index: number) => {
    if (searchValues.length <= 1) {
      setModalSearchValuesPosition(null);
    }
    const newSearchValue = [...searchValues];
    newSearchValue.splice(index, 1);
    setSearchValues(newSearchValue);

    setFirst(1);
  };

  useEffect(() => {
    // Find the index of the existing search action with the given id
    // console.log("values: "+JSON.stringify(searchValues))
    const searchIndex = cc.Searches.findIndex(search => search.identifier === id);

    // Create a new search action with the updated field
    const updatedSearch = new Search(id, selectedField, searchValues);

    // Update the cc.Searches array with the new search action
    if (searchIndex !== -1) {
      // Replace the existing search action with the updated one
      cc.Searches[searchIndex] = updatedSearch;
    } else {
      // Add the new search action to the array
      cc.Searches.push(updatedSearch);
    }

    setCc({
      ...cc,
      Searches: [...cc.Searches]
    });

  }, [selectedField, searchValues]);


  const handleFieldChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newField = event.target.value;

    // Update the local state with the new field
    setSelectedField(newField);

    setFirst(1);
  };

  const handleTypeAheadSelectItem = (event: React.SyntheticEvent, word: string) => {
    event.preventDefault();
    const newValue = { source: 'typeahead', text: word.trim() };

    // Find the index of the existing search action with the given id
    const searchIndex = cc.Searches.findIndex(search => search.identifier === id);

    // Create a new search action with only the new value
    const updatedSearch = new Search(id, selectedField, [newValue]);

    // Replace the cc.Searches array with the new search action
    cc.Searches = [];
    cc.Searches.push(updatedSearch);

    //if (searchIndex !== -1) {
    //  cc.Searches[searchIndex] = updatedSearch;
    //} else {
    //  cc.Searches.push(updatedSearch);
    //}

    setInputValue("");
    setFirst(1);
    setHighlightedIndex(null);
    setTypeAheadShowing(false);
    if (viewMode === "homepage") {
      setViewMode('list');
    }
  };

  const handleSearchButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    searchUserTypedText();
    if (viewMode == "homepage") {
      setViewMode('list');
    }
  };

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Update the inputValue state with the current value in the textbox
    setInputValue(event.target.value);
  };

  const searchUserTypedText = () => {
    if (inputValue.trim().length > 0) {
      // Create the new search value
      const newValue = { source: 'freetext', text: inputValue.trim() };

      // Find the index of the existing search action with the given id
      const searchIndex = cc.Searches.findIndex(search => search.identifier === id);

      // Create a new search action with only the new value
      const updatedSearch = new Search(id, selectedField, [newValue]);

      // Replace the cc.Searches array with the new search action
      cc.Searches = [];
      cc.Searches.push(updatedSearch);

      //if (searchIndex !== -1) {
      //  cc.Searches[searchIndex] = updatedSearch;
      //} else {
      //  cc.Searches.push(updatedSearch);
      //}

      // Reset the inputValue for the next search
      setInputValue("");

      setHighlightedIndex(null);
      setTypeAheadShowing(true);

      // Reset the pagination
      
      if (viewMode === "homepage") {
        setViewMode('list');
      }

      setFirst(1);
    }
  }


  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (typeAheadShowing) {
      switch (event.key) {
        case "ArrowDown":
          setHighlightedIndex((prevIndex) => (prevIndex === null || prevIndex === typeAheadResults.length - 1) ? 0 : prevIndex + 1);
          event.preventDefault();
          break;
        case "ArrowUp":
          setHighlightedIndex((prevIndex) => (prevIndex === null || prevIndex === 0) ? typeAheadResults.length - 1 : prevIndex - 1);
          event.preventDefault();
          break;
        case "Enter":
          if (highlightedIndex !== null) {
            handleTypeAheadSelectItem(event, typeAheadResults[highlightedIndex].word);
            event.preventDefault();
          } else if (inputValue.trim()) {
            searchUserTypedText();
            if (viewMode == "homepage") {
              setViewMode('list');
            }
          }
          break;
        case "Escape":
          setTypeAheadShowing(false);
          setHighlightedIndex(null);
          break;
        default:
          setTypeAheadShowing(true);
          break;
      }
    } else if (event.key === "Enter" && inputValue.trim()) {
      event.preventDefault();
      searchUserTypedText();
    } else {
      setHighlightedIndex(null);
      setTypeAheadShowing(true);
    }
  };

  useEffect(() => {
    if (inputValue) {
      lastFetchedInputRef.current = inputValue;
      let adaptedInputValue = inputValue.trim();
      const query = ccUtils.generateQuery(cc, user, { excludeSimpleSearch: true });
      let queryField = "";
      if (!Fields || Fields === "simple") {
        queryField = ccConfig.simpleSearchFields[selectedField as keyof typeof ccConfig.simpleSearchFields]?.field;
      }
      if ((!Fields && (!queryField || queryField === "")) || Fields === "advanced") {
        queryField = ccConfig.simpleSearchFields[selectedField as keyof typeof ccConfig.simpleSearchFields]?.field;
      }

      const postData = {
        action: "get",
        command: "scanconcepts",
        fields: queryField,
        query: query,
        mask: "regexp(.*"+adaptedInputValue+".*)",
        range: "1-10",
        responseformat: "json"
      };

      ccUtils.postWithCache(process.env.REACT_APP_BASE_URL as string, postData, instanceRef)
        .then(data => {
          if (inputValue === lastFetchedInputRef.current) {
            if (data.word.word && Array.isArray(data.word.word)) {
              setTypeAheadResults(data.word.word);
            } else {
              setTypeAheadResults([]);
              setTypeAheadShowing(false);
            }
          }
        }).catch(error => {
          console.error(error);
        });
    } else {
      setTypeAheadResults([]);
      setTypeAheadShowing(false);
    }
  }, [inputValue]);

  useEffect(() => {
    // Check if cc.Searches is not empty and has a value for the first search
    if (cc.Searches && cc.Searches.length > 0 && cc.Searches[0].searchValues && cc.Searches[0].searchValues.length > 0) {
      // Retrieve the first search value and its source
      const firstSearchValue = cc.Searches[0].searchValues[0];
      const valueText = firstSearchValue.text;
      const valueSource = firstSearchValue.source;
  
      // Check if the source is 'typeahead' or 'facet' and adjust the value accordingly
      if (valueSource === 'typeahead' || valueSource === 'facet') {
        setInputValue(`"${valueText}"`); // Adds double quotes around the value
      } else {
        setInputValue(valueText);
      }
    } else {
      setInputValue("");
    }
  }, [cc.Searches]); // Dependency array ensures this runs when cc.Searches changes
  
  const renderTypeAheadResults = () => {
    let x = 0;
    let y = 0;

    if (inputTextBoxRef.current) {
      const rect = inputTextBoxRef.current.getBoundingClientRect();

      // Try to find a parent modal of the input box
      const parentModal = inputTextBoxRef.current.closest('.parentModalClass');
      if (parentModal) {
        const modalRect = parentModal.getBoundingClientRect();
        x = rect.left - modalRect.left + window.scrollX;
        y = rect.bottom + 2 - modalRect.top + window.scrollY;
      } else {
        x = rect.left + window.scrollX;
        y = rect.bottom + 2 + window.scrollY;
      }
    }

    return (
      <div
        className={styles.typeAheadResults}
        ref={typeAheadRef}
        style={{
          position: 'absolute',
          top: y + 'px',
          left: x + 'px'
        }}
      >
        {typeAheadResults.map((item: TypeAheadItem, index: number) => (
          <div
            key={index}
            className={`${styles.typeAheadItem} ${highlightedIndex === index ? styles.highlightedItem : ''}`}
            onClick={(event) => handleTypeAheadSelectItem(event, item.word)}
          >
            {item.word}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.SimpleSearchBox}>
      {Field ? (
        <></>
      ) : (
        false ? ( //show this on the results page, but not on the homepage?
          <label>
            <select className={genericStyles.genericSelect} value={selectedField} onChange={handleFieldChange}>
              {Object.keys(searchFields).map((key, index) => (
                <option key={index} value={key}>{t(key)}</option>
              ))}
            </select>
          </label>
        ) : (
          <></>
        )
      )}
      <div className={styles.searchValueContainer}>
        <div className={styles.textBox}>
          <input
            ref={inputTextBoxRef}
            className={styles.SimpleSearchTextbox}
            type="text"
            placeholder={t("simplesearchbox_new_search")}
            value={inputValue}
            onChange={handleValueChange}
            onKeyDown={handleKeyDown}
          />
        </div>
      </div>
      <GenericIconButton icon='light/times.svg' width='22px' height='22px' onClick={() => clearSearch()} />
      {showSearchButton &&
        <div className={styles.homePageButton}>
          <GenericIconButton
            icon='regular/search.svg'
            hoverEnabled={true}
            width='22px'
            height='22px'
            onClick={ handleSearchButtonClick }
          />
        </div>}
      {ccConfig.simpleSearchFields[selectedField]?.browse && (
        <>
          <button className={genericStyles.GenericButton} onClick={openBrowseValuesModal}>
            {t("searchbox_browse_button")}
          </button>

          {isBrowseValuesModalOpen &&
            <BrowseValues
              field={ccConfig.simpleSearchFields[selectedField]?.field}
              sort='alpha'
              searchValues={searchValues.filter(value => value.source === 'facet').map(value => value.text)}
              onSelect={handleLocalSelectedBrowseValues}
              isOpen={isBrowseValuesModalOpen}
              onClose={closeBrowseValuesModal}
            />
          }
        </>
      )}
      <button className={`${genericStyles.GenericButton} ${styles.advancedSearchButton}`} onClick={openAdvancedModal}>
        {t("searchbox_advanced_button")}
      </button>

      {typeAheadShowing && renderTypeAheadResults()}

      <AdvancedSearch
        prevCc={prevCc}
        setPrevCc={setPrevCc}
        isOpen={isAdvancedModalOpen}
        onClose={closeAdvancedModal}
      />
    </div>
  );
}

export default SimpleSearchBox;
